import React from "react";
import { FileText, Wallet, Trash2, Ban, ReceiptText, Pencil, Coins, FileMinus } from "lucide-react";
import { inr } from "../../lib/api";
import { LockBadge } from "../ApprovalLock";
import { statusColors } from "./shared";

const COLS = "grid grid-cols-[20px_140px_minmax(0,1fr)_110px_190px_110px_270px] gap-4 px-5";
const iconBtn = (hover) => `text-muted-foreground ${hover} transition-colors`;

const PartyCell = ({ inv }) => (
  <div className="min-w-0">
    <div className="font-medium break-words">{inv.account_name || inv.party_name}{inv.account_no ? <span className="num ml-1 text-[10px] bg-primary/10 text-primary px-1 py-0.5 rounded">{inv.account_no}</span> : null}{inv.invoice_type ? <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground whitespace-nowrap">{inv.invoice_type}</span> : null}</div>
    {inv.account_name && inv.party_name && inv.party_name !== inv.account_name ? <div className="text-xs text-muted-foreground break-words">Party: {inv.party_name}</div> : null}
    {inv.driver_mobile ? <div className="num text-xs text-muted-foreground">Driver {inv.driver_mobile}</div> : null}
    <div className="text-xs text-muted-foreground num truncate">{inv.date}{inv.vehicle_number ? ` · ${inv.vehicle_number}` : ""}{inv.job_card_number ? ` · JC ${inv.job_card_number}` : ""} {inv.doc_status === "cancelled" ? <span className="ml-1 px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive">cancelled</span> : <span className={`ml-1 px-1.5 py-0.5 rounded-full capitalize ${statusColors[inv.status]}`}>{inv.status}</span>}</div>
    {inv.doc_status === "cancelled" && inv.cancel_reason ? <div className="text-xs text-destructive truncate">Reason: {inv.cancel_reason}</div> : null}
  </div>
);

const AmountCells = ({ inv }) => (
  <>
    <div className="text-right">
      <div className="num text-sm">{inr(inv.amount)}</div>
      {inv.discount > 0 ? <div className="text-[10px] text-accent num">− Disc {inr(inv.discount)}</div> : null}
    </div>
    <div className="text-right">
      <div className="num text-sm text-[hsl(var(--positive))]">{inr(inv.paid_amount)}</div>
      {(inv.paid_cash > 0 || inv.paid_online > 0) ? <div className="text-[10px] text-muted-foreground num whitespace-nowrap">C {inr(inv.paid_cash)} · O {inr(inv.paid_online)}</div> : null}
    </div>
    <span className={`num text-sm text-right font-bold ${inv.balance > 0 ? "text-destructive" : ""}`}>{inr(inv.balance)}</span>
  </>
);

const RowActions = ({ inv, canWrite, act }) => {
  const live = canWrite && inv.doc_status !== "cancelled";
  return (
    <div className="flex flex-wrap items-center gap-2 justify-end">
      <button data-testid={`inv-receipt-${inv.id}`} onClick={() => act.receipt(inv)} title={inv.receipt_no ? `Receipt ${inv.receipt_no}` : "Receipt"} className={iconBtn("hover:text-accent")}><ReceiptText size={15} /></button>
      {live && <button data-testid={`inv-paymode-${inv.id}`} onClick={act.guard(inv, act.payMode)} title="How payment was received (cash/UPI/bank/QR)" className={iconBtn("hover:text-accent")}><Coins size={15} /></button>}
      {live && <button data-testid={`inv-edit-${inv.id}`} onClick={act.guard(inv, act.edit)} title="Edit invoice" className={iconBtn("hover:text-primary")}><Pencil size={15} /></button>}
      {live && (inv.balance > 0 || inv.paid_amount > 0) && (
        <button data-testid={`inv-pay-${inv.id}`} onClick={act.guard(inv, act.pay)} className="h-9 px-3 rounded-md bg-primary text-primary-foreground text-sm flex items-center gap-1.5 hover:bg-primary/90 transition-colors">
          <Wallet size={14} /> {inv.balance > 0 ? "Pay" : "Payments"}
        </button>
      )}
      {live && <button data-testid={`inv-creditnote-${inv.id}`} onClick={act.guard(inv, act.creditNote)} title="Credit note against this invoice" className={iconBtn("hover:text-accent")}><FileMinus size={15} /></button>}
      {live && <button data-testid={`inv-cancel-${inv.id}`} onClick={act.guard(inv, act.cancel)} title="Cancel invoice" className={iconBtn("hover:text-destructive")}><Ban size={15} /></button>}
      {canWrite && <button data-testid={`inv-del-${inv.id}`} onClick={act.guard(inv, act.del)} className={iconBtn("hover:text-destructive")}><Trash2 size={15} /></button>}
    </div>
  );
};

const InvoiceRow = ({ inv, checked, onToggle, lock, onLockClick, canWrite, act }) => (
  <div data-testid={`inv-row-${inv.id}`} className={`${COLS} py-4 items-center hover:bg-background/50 transition-colors ${checked ? "bg-accent/5" : ""}`}>
    <input data-testid={`inv-select-${inv.id}`} type="checkbox" checked={checked} onChange={onToggle} className="h-4 w-4 accent-[hsl(var(--primary))]" />
    <span className="num text-sm font-bold text-primary flex flex-col items-start gap-0.5 min-w-0 break-all">{inv.invoice_no}{lock && <LockBadge info={lock} testid={`inv-lock-${inv.id}`} onClick={onLockClick} />}</span>
    <PartyCell inv={inv} />
    <AmountCells inv={inv} />
    <RowActions inv={inv} canWrite={canWrite} act={act} />
  </div>
);

export const InvoiceTable = ({ invoices, selected, toggleSel, allSelected, toggleAll, locks, setLockInfo, canWrite, act }) => {
  if (invoices.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
        <FileText className="mx-auto mb-3 opacity-40" size={32} /> No invoices here. Create invoices from the Cash Book.
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-border bg-card overflow-x-auto animate-fade-up">
      <div className="min-w-[1040px]">
        <div className={`${COLS} py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground`}>
          <input data-testid="inv-select-all" type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-[hsl(var(--primary))]" />
          <span>No.</span><span>Account</span><span className="text-right">Amount</span><span className="text-right">Paid</span><span className="text-right">Balance</span><span className="text-right">Actions</span>
        </div>
        <div className="divide-y divide-border">
          {invoices.map((inv) => (
            <InvoiceRow key={inv.id} inv={inv} checked={selected.has(inv.id)} onToggle={() => toggleSel(inv.id)}
              lock={locks[inv.id]} onLockClick={() => setLockInfo(locks[inv.id])} canWrite={canWrite} act={act} />
          ))}
        </div>
      </div>
    </div>
  );
};
